/**
 * The owner's classification of a review – metadata only, never content.
 * An email draft has a topic and a priority; a document has a topic (its
 * priority comes from the P<n> filename). Both share the owner's topic catalog.
 */
import { useId, useState } from "react";
import { adminApi, type AdminReviewDoc } from "../../api/admin";
import { apiErrorDetail } from "../../api/client";
import { PRIORITY_LABELS } from "../../lib/emailDrafts";
import { PriorityBadge } from "../PriorityBadge";

export function EmailDraftPriority({ priority }: { priority?: number | null }) {
  return <PriorityBadge priority={priority} long />;
}

export function ReviewTriage({ doc }: { doc: AdminReviewDoc }) {
  const isEmail = doc.kind === "email";
  const [editing, setEditing] = useState<{ topic: string; priority: string } | null>(null);
  const topics = adminApi.aiTeam.reviewTopics();
  const update = adminApi.aiTeam.updateReviewTriage();
  const id = useId();
  if (!editing) {
    return <button className="btn btn-sm btn-ghost" type="button"
      aria-label={isEmail ? `Edit topic and priority for ${doc.title}` : `Edit topic for ${doc.title}`}
      onClick={() => { update.reset(); setEditing({ topic: doc.topic?.name ?? "", priority: String(doc.priority ?? "") }); }}>
      {isEmail ? "Edit classification" : "Edit topic"}
    </button>;
  }
  return <form className="flex items-end gap-2 flex-wrap" onSubmit={(event) => {
    event.preventDefault();
    const patch = isEmail
      ? { topic_name: editing.topic, priority: Number(editing.priority) }
      : { topic_name: editing.topic };
    update.mutate({ reviewId: doc.id, patch }, { onSuccess: () => setEditing(null) });
  }}>
    <label className="text-xs text-ink-low" htmlFor={`${id}-topic`}>Topic
      <input id={`${id}-topic`} list={`${id}-topics`} className="input block" required maxLength={120}
        placeholder="Choose or add a topic" value={editing.topic}
        onChange={(event) => setEditing({ ...editing, topic: event.target.value })} />
    </label>
    <datalist id={`${id}-topics`}>{topics.data?.map((topic) => <option key={topic.id} value={topic.name} />)}</datalist>
    {isEmail ? (
      <label className="text-xs text-ink-low" htmlFor={`${id}-priority`}>Priority
        <select id={`${id}-priority`} className="input block" required value={editing.priority}
          onChange={(event) => setEditing({ ...editing, priority: event.target.value })}>
          <option value="" disabled>Choose priority</option>
          {Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
    ) : null}
    <button className="btn btn-sm btn-main" disabled={update.isPending || !editing.topic.trim() || (isEmail && !editing.priority)}>Save</button>
    <button className="btn btn-sm btn-ghost" type="button" disabled={update.isPending} onClick={() => setEditing(null)}>Cancel</button>
    {topics.isError ? <span className="text-xs text-danger">Topics unavailable: {apiErrorDetail(topics.error)}</span> : null}
    {update.isError ? <span className="text-xs text-danger" role="alert">{apiErrorDetail(update.error)}</span> : null}
  </form>;
}
